import { supabase } from './supabase';

/**
 * Business accounts — the one place the website talks to the organisation
 * tables. Ported from the mobile app's `lib/organizations.ts` so both clients
 * speak to the same schema in the same vocabulary.
 *
 * Every function assumes the RLS from the 20260828 migrations is in place.
 * Nothing here re-checks permissions in JavaScript: the database decides, so
 * a bug in this file cannot widen access. What it does is give the UI a typed
 * vocabulary and turn Postgres errors into sentences a person can read.
 */

export type OrgRole = 'owner' | 'admin' | 'driver';

export interface Organization {
  id: string;
  name: string;
  plan: string;
  seat_limit: number;
  vehicle_limit: number;
  created_by: string;
  created_at: string;
}

export interface OrgMembership {
  organization: Organization;
  role: OrgRole;
}

export interface OrgMember {
  id: string;
  user_id: string;
  role: OrgRole;
  created_at: string;
  full_name?: string | null;
  /** How a manager tells two drivers with the same first name apart. */
  email?: string | null;
}

export interface OrgInvite {
  /** Set by inviteMember: whether the invitation email actually went out. */
  emailed?: boolean;
  id: string;
  email: string;
  role: Exclude<OrgRole, 'owner'>;
  token: string;
  accepted_at: string | null;
  expires_at: string;
  created_at: string;
}

/** Managers can change the fleet; drivers can only record against their own. */
export const canManage = (role?: OrgRole | null) => role === 'owner' || role === 'admin';

function readableError(error: any): Error {
  const msg: string = error?.message ?? String(error);
  if (msg.includes('seat limit reached')) {
    return new Error('This plan has no seats left. Add a seat to invite another driver.');
  }
  if (msg.includes('organization_members_one_owner')) {
    return new Error('This organisation already has an owner.');
  }
  if (msg.includes('invite already used')) return new Error('That invitation has already been used.');
  if (msg.includes('invite expired')) return new Error('That invitation has expired. Ask for a new one.');
  if (msg.includes('different email address')) {
    return new Error('That invitation was sent to a different email address. Sign in with that address to accept it.');
  }
  if (msg.includes('organization_invites_organization_id_email_key')) {
    return new Error('That person has already been invited.');
  }
  if (msg.includes('NAME_REQUIRED')) return new Error('Give your business a name.');
  if (msg.includes('NAME_TOO_LONG')) return new Error('That name is too long — 120 characters at most.');
  if (msg.includes('NOT_SIGNED_IN')) {
    return new Error('You need to be signed in to create a business account.');
  }
  if (msg.includes('NOT_A_MEMBER')) {
    return new Error('You are not part of this business account.');
  }
  if (msg.includes('NOT_A_MANAGER')) {
    return new Error('Only the owner or an admin can see this.');
  }
  return new Error(msg);
}

export async function getMyOrganizations(): Promise<OrgMembership[]> {
  const { data, error } = await supabase
    .from('organization_members')
    .select('role, organizations!inner(id, name, plan, seat_limit, vehicle_limit, created_by, created_at)')
    .order('created_at', { ascending: true });
  if (error) throw readableError(error);
  return (data ?? []).map((r: any) => ({ role: r.role as OrgRole, organization: r.organizations }));
}

export async function createOrganization(name: string): Promise<Organization> {
  // Goes through the database function, not a direct insert.
  //
  // `.insert(...).select()` failed with "new row violates row-level security
  // policy": the RETURNING clause needs the SELECT policy, which is
  // `is_org_member(id)`, and the trigger that makes the caller the owner is
  // AFTER INSERT — it fires at the end of the statement, a moment after
  // RETURNING is evaluated. So the row could be created but never read back,
  // and no business account could be made at all.
  const { data, error } = await supabase.rpc('create_organization', { p_name: name.trim() });
  if (error) throw readableError(error);
  return data as Organization;
}

export async function listMembers(organizationId: string): Promise<OrgMember[]> {
  // Goes through org_roster rather than selecting the table directly.
  // organization_members.user_id references auth.users, not profiles, so
  // PostgREST has no foreign key to follow and cannot embed the name — and
  // even with one, profiles RLS lets a person read only their own row. The
  // roster listed everyone as "Driver", including the owner. The function is
  // SECURITY DEFINER, checks membership, and returns only this organisation.
  const { data, error } = await supabase.rpc('org_roster', { p_org_id: organizationId });
  if (error) throw readableError(error);
  return (data ?? []) as OrgMember[];
}

export async function listInvites(organizationId: string): Promise<OrgInvite[]> {
  const { data, error } = await supabase
    .from('organization_invites')
    .select('id, email, role, token, accepted_at, expires_at, created_at')
    .eq('organization_id', organizationId)
    .is('accepted_at', null)
    .order('created_at', { ascending: false });
  if (error) throw readableError(error);
  return (data ?? []) as OrgInvite[];
}

export async function inviteMember(
  organizationId: string,
  email: string,
  role: Exclude<OrgRole, 'owner'> = 'driver',
): Promise<OrgInvite> {
  const { data: auth } = await supabase.auth.getUser();
  const uid = auth?.user?.id;
  if (!uid) throw new Error('You need to be signed in.');

  // Stored lower-case because acceptance compares it to the caller's email.
  const { data, error } = await supabase
    .from('organization_invites')
    .insert({
      organization_id: organizationId,
      email: email.trim().toLowerCase(),
      role,
      invited_by: uid,
    })
    .select('id, email, role, token, accepted_at, expires_at, created_at')
    .single();
  if (error) throw readableError(error);

  // Send the email. Deliberately not awaited into the error path: the invite
  // row is what grants access, so a mail outage must not make a successful
  // invitation look failed. The UI reports whether it went out.
  let emailed = false;
  try {
    const { data: mail } = await supabase.functions.invoke('send-org-invite', {
      body: { invite_id: (data as any).id },
    });
    emailed = Boolean((mail as any)?.sent);
  } catch {
    emailed = false;
  }

  return { ...(data as OrgInvite), emailed };
}

export async function revokeInvite(inviteId: string): Promise<void> {
  const { error } = await supabase.from('organization_invites').delete().eq('id', inviteId);
  if (error) throw readableError(error);
}

/**
 * Accept an invitation. Goes through the database function rather than a
 * direct insert: the accepting user is not yet a member, so no policy can let
 * them read their own invite without exposing everyone else's.
 */
export async function acceptInvite(token: string): Promise<string> {
  const { data, error } = await supabase.rpc('accept_organization_invite', { invite_token: token });
  if (error) throw readableError(error);
  return data as string;
}

export async function removeMember(memberId: string): Promise<void> {
  const { error } = await supabase.from('organization_members').delete().eq('id', memberId);
  if (error) throw readableError(error);
}

export async function setMemberRole(memberId: string, role: Exclude<OrgRole, 'owner'>): Promise<void> {
  const { error } = await supabase.from('organization_members').update({ role }).eq('id', memberId);
  if (error) throw readableError(error);
}

export async function assignVehicle(
  organizationId: string,
  vehicleId: string,
  userId: string,
): Promise<void> {
  const { error } = await supabase
    .from('organization_vehicle_assignments')
    .insert({ organization_id: organizationId, vehicle_id: vehicleId, user_id: userId });
  if (error) throw readableError(error);
}

export async function unassignVehicle(vehicleId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('organization_vehicle_assignments')
    .delete()
    .eq('vehicle_id', vehicleId)
    .eq('user_id', userId);
  if (error) throw readableError(error);
}

export interface FleetVehicle {
  id: string;
  year: string;
  make: string;
  model: string;
  trim: string | null;
  current_mileage?: number | null;
  health_score?: number | null;
}

/** Every vehicle owned by the organisation. Managers only, enforced by RLS. */
export async function listFleetVehicles(organizationId: string): Promise<FleetVehicle[]> {
  const { data, error } = await supabase
    .from('vehicles')
    .select('id, year, make, model, trim, current_mileage, health_score')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: true });
  if (error) throw readableError(error);
  return (data ?? []) as FleetVehicle[];
}

export async function listAssignedVehicleIds(
  organizationId: string,
  userId: string,
): Promise<string[]> {
  const { data, error } = await supabase
    .from('organization_vehicle_assignments')
    .select('vehicle_id')
    .eq('organization_id', organizationId)
    .eq('user_id', userId);
  if (error) throw readableError(error);
  return (data ?? []).map((r: any) => r.vehicle_id);
}

/** Seat usage and the plan, for the invite screen and for billing gating. */
export async function getSeatUsage(
  organizationId: string,
): Promise<{ used: number; limit: number; plan: string }> {
  const [{ count, error: cErr }, { data: org, error: oErr }] = await Promise.all([
    supabase
      .from('organization_members')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', organizationId),
    supabase.from('organizations').select('seat_limit, plan').eq('id', organizationId).single(),
  ]);
  if (cErr) throw readableError(cErr);
  if (oErr) throw readableError(oErr);
  return {
    used: count ?? 0,
    limit: (org as any)?.seat_limit ?? 0,
    plan: (org as any)?.plan ?? 'free',
  };
}

// ── Manager-only reporting ───────────────────────────────────────────────────
// Both of these are SECURITY DEFINER functions that raise NOT_A_MANAGER rather
// than returning an empty result, so a driver who reaches them sees a refusal
// instead of a fleet that appears to have spent nothing.

export interface FleetSpendRow {
  vehicle_id: string;
  label: string;
  fuel_total: number;
  maintenance_total: number;
  total: number;
  entries: number;
}

export async function fleetSpendSummary(
  organizationId: string,
  since?: string | null,
): Promise<FleetSpendRow[]> {
  const { data, error } = await supabase.rpc('fleet_spend_summary', {
    p_org_id: organizationId,
    p_since: since ?? null,
  });
  if (error) throw readableError(error);
  return (data ?? []).map((r: any) => ({
    ...r,
    fuel_total: Number(r.fuel_total ?? 0),
    maintenance_total: Number(r.maintenance_total ?? 0),
    total: Number(r.total ?? 0),
    entries: Number(r.entries ?? 0),
  }));
}

export interface FleetActivityRow {
  kind: 'fuel' | 'maintenance';
  occurred_at: string;
  actor_id: string | null;
  actor_name: string;
  vehicle_id: string;
  vehicle_label: string;
  detail: string;
  amount: number | null;
}

export async function fleetActivity(
  organizationId: string,
  limit = 100,
): Promise<FleetActivityRow[]> {
  const { data, error } = await supabase.rpc('fleet_activity', {
    p_org_id: organizationId,
    p_limit: limit,
  });
  if (error) throw readableError(error);
  return (data ?? []).map((r: any) => ({
    ...r,
    amount: r.amount == null ? null : Number(r.amount),
  }));
}

/**
 * Pull an entire RPC result, however long it is.
 *
 * PostgREST caps a response at 1000 rows, and it does it to `.rpc()` just as
 * much as to `.select()` — the function returns everything, the transport
 * truncates it, and nothing anywhere reports a problem. This has bitten twice
 * already in this codebase, both times spotted only because the count came back
 * as a suspiciously round 1000.
 *
 * That is survivable in a feed and unacceptable in a report: a printed ledger
 * that silently stops at the thousandth line still looks complete, still adds
 * up to a total, and is wrong. So reports page through.
 */
async function rpcAll<T>(fn: string, params: Record<string, unknown>): Promise<T[]> {
  const PAGE = 1000;
  const out: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase.rpc(fn, params).range(from, from + PAGE - 1);
    if (error) throw readableError(error);
    const page = (data ?? []) as T[];
    out.push(...page);
    if (page.length < PAGE) return out;
  }
}

export interface FleetLedgerRow {
  kind: 'fuel' | 'maintenance';
  occurred_on: string;
  vehicle_id: string;
  vehicle_label: string;
  detail: string;
  place: string;
  mileage: number | null;
  amount: number | null;
  logged_by_name: string;
}

/**
 * Every line item in a date window — the thing a report is printed from.
 *
 * Not fleetActivity: that one takes a row limit and is ordered newest-first, so
 * printing from it drops the oldest entries and never says so.
 */
export async function fleetLedger(
  organizationId: string,
  since?: string | null,
  until?: string | null,
): Promise<FleetLedgerRow[]> {
  const rows = await rpcAll<any>('fleet_ledger', {
    p_org_id: organizationId,
    p_since: since ?? null,
    p_until: until ?? null,
  });
  return rows.map((r) => ({
    ...r,
    mileage: r.mileage == null ? null : Number(r.mileage),
    amount: r.amount == null ? null : Number(r.amount),
  }));
}

export interface FleetDocumentRow {
  kind: 'document' | 'photo';
  id: string;
  vehicle_id: string;
  vehicle_label: string;
  title: string;
  category: string;
  file_url: string;
  file_name: string;
  file_size: number | null;
  file_type: string | null;
  uploaded_at: string;
  uploaded_by_name: string;
}

/** Everything the fleet has ever uploaded — documents and vehicle photos. */
export async function fleetDocuments(organizationId: string): Promise<FleetDocumentRow[]> {
  const rows = await rpcAll<any>('fleet_documents', { p_org_id: organizationId });
  return rows.map((r) => ({
    ...r,
    file_size: r.file_size == null ? null : Number(r.file_size),
  }));
}
